import { useState } from "react";
import { useSubscription } from "@/context/SubscriptionContext";
import PricingDialog from "@/components/PricingDialog";
import { Lock, Sparkles, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Wraps any premium feature. Shows the full feature if isPro,
 * otherwise a locked shell with a "Start free trial" CTA.
 */
export default function Paywall({ feature = "This feature", description = "Unlock with a 7-day free trial", children }) {
  const { isPro, inTrial, daysLeft, loading } = useSubscription();
  const [open, setOpen] = useState(false);
  if (loading) return null;
  if (isPro) return children;

  return (
    <section className="space-y-6" data-testid="paywall">
      <div className="circa-card p-8 sm:p-10 text-center relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-indigo-500/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-56 h-56 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />

        <div className="relative">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center glow-indigo mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <div className="pill mx-auto mb-3 bg-indigo-950/70 border-indigo-500/40 text-indigo-200 w-fit">
            <Crown className="w-3.5 h-3.5" /> Pro feature
          </div>
          <h3 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">{feature}</h3>
          <p className="text-slate-400 text-sm mt-2 max-w-md mx-auto leading-relaxed">{description}</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center items-center">
            <Button
              onClick={() => setOpen(true)}
              data-testid="btn-open-pricing"
              className="rounded-full bg-indigo-500 hover:bg-indigo-400 text-white glow-indigo px-6 h-11"
            >
              <Sparkles className="w-4 h-4 mr-1" /> Start free 7-day trial
            </Button>
            {inTrial && daysLeft > 0 && (
              <span className="text-xs text-emerald-300 font-mono-ui uppercase tracking-widest">
                Trial active · {daysLeft}d left
              </span>
            )}
          </div>
          <p className="text-[10px] uppercase tracking-widest font-mono-ui text-slate-500 mt-4">
            Cancel anytime · Monthly $3.99 or Yearly $39
          </p>
        </div>
      </div>

      <PricingDialog open={open} onOpenChange={setOpen} />
    </section>
  );
}
